import axios, { AxiosResponse } from 'axios';
import Pagination from '@/domain/pagination.entity';
import Response from '@/domain/response.entity';
import BillingContractsSelected from '@/domain/billing/entity/billing-contract-selected.entity';
import DateHandler from '@/shared/utils/date-format';
import RobotFilterType from '@/domain/contract/entity/robot-filter-type.enum';
import { ContractPeriodFilterType } from '@/domain/contract/entity/contract-period-filter-type.enum';
import { ContractGroupLinkedType } from '@/domain/contract/entity/contract-group-linked-type.enum';
import { BillingDailyFilterType } from '@/domain/contract/entity/billing-daily-filter-type.enum';
import { BillingAnniversaryFilterType } from '@/domain/contract/entity/billing-anniversary-filter-type.enum';
import Summary from '../entity/summary.entity';
import BillingFilterRequest from '../request/billing-filter-request.entity';
import BillingInvoiceUrl from '../entity/billing-invoice-url.entity';
import BillingInvoiceCompetence from '../entity/billing-invoice-competence.entity';
import BillingDetailed from '../entity/billing-detailed.entity';

export type ExecuteBillingParamsType = {
  businessUnitId: string;
  invoiceCompetence: string;
  billingDaily: boolean;
}

export type ExecuteBillingContractSelectedRequestType = {
  businessUnitId: string;
  invoiceCompetence: string;
  beneficiaryId: string;
  contractNumber: string;
  customerDocument: string;
  customerIds: string[];
  externalId: number;
  instanceCode: string;
  resultCenterId: string;
  periodStart: string | null;
  periodEnd: string | null;
  billingAnniversaryType: BillingAnniversaryFilterType;
  anniversaryPeriodStart: string | null;
  anniversaryPeriodEnd: string | null;
  status: string;
  productIds: string | null;
  markedToExpire: boolean;
  frequency: string | null;
  isCustomBilling: boolean;
  isReviewed: boolean;
  robotFilterType: RobotFilterType;
  contractGroupId: string;
  contractGroupLinkedType: ContractGroupLinkedType;
  billingDaily: BillingDailyFilterType;
  competenceFilter: string;
  expiresAtStart: string | null;
  expiresAtEnd: string | null;
  filterInterval: ContractPeriodFilterType;
  contractModel: string | null;
}
export default class BillingService {
  public static getParamsFromFilter(filter: BillingFilterRequest): any {
    const params = {
      customerExternalId: filter.customerExternalId,
      customerIds: filter.customerIds.length ? filter.customerIds : null,
      searchBy: filter.searchBy,
      searchValue: filter.searchValue,
      resultCenterId: filter.resultCenterId,
      competence: filter.competence,
      invoiceNumber: filter.invoiceNumber,
      status: filter.status,
      netValueOperator: filter.netValueOperator,
      netValue: filter.netValue,
      dueDateStart: filter.dueDateStart ? DateHandler.formatToIsoDate(filter.dueDateStart) : null,
      dueDateEnd: filter.dueDateEnd ? DateHandler.formatToIsoDate(filter.dueDateEnd) : null,
      anniversary: filter.anniversary,
      beneficiary: filter.beneficiary,
      product: filter.product,
      discountedValueOperator: filter.discountedValueOperator,
      discountedValue: filter.discountedValue,
      contractExternalId: filter.contractExternalId,
      invoicePaymentExternalIds: filter.invoicePaymentExternalIds,
      productId: filter.productId,
      frequency: filter.frequency,
    }
    return params;
  }

  public static getAllToExport(businessUnitId: string, filter: BillingFilterRequest,
    pagination: Pagination): Promise<Response> {
    const itemsPerPage = 100;
    const totalPromisses = Math.trunc(pagination.total / itemsPerPage)
      + ((pagination.total % itemsPerPage) === 0 ? 0 : 1);

    const tasks : Promise<any>[] = [];
    let lastSize = 0;
    for (let pageNumber = 1; pageNumber <= totalPromisses; pageNumber += 1) {
      let size = itemsPerPage;
      lastSize += itemsPerPage;
      if (lastSize > pagination.total) {
        size = pagination.total - (lastSize - itemsPerPage);
      }
      const params = {
        ...this.getParamsFromFilter(filter),
        page: pageNumber - 1,
        size,
        sortBy: pagination.arrangeSortBy,
      };
      tasks.push(axios.get(`/api/v1/business-units/${businessUnitId}/invoices`, { params }));
    }

    return Promise.all(tasks).then((response: any) => {
      const responseData = response.map((item) => item.data);
      const contentList : any[] = responseData.flatMap((item) => item.content);
      const responseResult = {
        totalElements: contentList.length,
        totalPages: pagination.total,
        content: contentList,
      };
      return Response.of(responseResult);
    });
  }

  public static exportToEmailInvoiceByCompetence(
    businessUnitId: string,
    userEmail: string,
    filter: BillingInvoiceCompetence,
  ): void {
    const params = {
      competence: filter.competence,
      competenceAt: filter.competenceAt,
      recipients: userEmail,
    }
    axios.get(`/api/v1/business-units/${businessUnitId}/invoices/export-email-competence`, { params })
      .then((response: any) => Response.of(response.data));
  }

  public static downloadFilePost(
    businessUnitId: string,
    filter: BillingFilterRequest,
    pagination: Pagination,
    invoiceReportType: string,
  ): Promise<any> {
    filter = this.getParamsFromFilter(filter);
    const params = {
      page: pagination.page - 1,
      size: pagination.itemsPerPage,
      invoiceReportType,
    }
    return axios
      .post(`/api/v1/business-units/${businessUnitId}/invoices/download-file`, filter, { params })
      .then((response: any) => response.data);
  }

  public static exportToEmailPost(
    businessUnitId: string,
    userEmail: string,
    filter: BillingFilterRequest,
    pagination: Pagination,
    invoiceReportType?: string,
  ): void {
    filter = this.getParamsFromFilter(filter);
    const params = {
      recipients: userEmail,
      page: pagination.page - 1,
      size: pagination.itemsPerPage,
      sortBy: pagination.arrangeSortBy,
      invoiceReportType: invoiceReportType || null,
    }

    axios.post(`/api/v1/business-units/${businessUnitId}/invoices/export-email`, filter, { params })
  }

  public static getAllPost(
    businessUnitId: string,
    filter: BillingFilterRequest,
    pagination: Pagination,
  ) : Promise<Response> {
    const filterFormatted = this.getParamsFromFilter(filter);
    filterFormatted.customerIds = filter.customerIds || [];
    const params = {
      page: pagination.page - 1,
      size: pagination.itemsPerPage,
      sortBy: pagination.arrangeSortBy,
    }

    return axios
      .post(`/api/v1/business-units/${businessUnitId}/invoices`, filterFormatted, { params })
      .then((response: any) => Response.of(response.data));
  }

  public static getAllSimplifiedPost(
    businessUnitId: string,
    filter: BillingFilterRequest,
    pagination: Pagination,
  ): Promise<Response> {
    const filterFormatted = this.getParamsFromFilter(filter);
    filterFormatted.customerIds = filter.customerIds || [];
    const params = {
      page: pagination.page - 1,
      size: pagination.itemsPerPage,
      sortBy: pagination.arrangeSortBy,
    };
    return axios
      .post(`/api/v1/business-units/${businessUnitId}/invoices-simplified`, filterFormatted, { params })
      .then((response: any) => Response.of(response.data));
  }

  public static getById(billingId: string): Promise<BillingDetailed> {
    return axios.get(`/api/v1/business-units/invoices/${billingId}`)
      .then(({ data }) => BillingDetailed.of(data));
  }

  public static getBillingSummary(
    businessId: string, competence: string,
  ): Promise<Summary> {
    return axios
      .get(`/api/v1/business-units/${businessId}/invoices/summary`, {
        params: {
          competence,
        },
      })
      .then((response: any) => Summary.of(response.data));
  }

  public static executeBilling(params: ExecuteBillingParamsType): Promise<string> {
    return axios
      .post(
        `/api/v1/business-units/${params.businessUnitId}/calculations/perform`,
        null,
        {
          params: {
            ...params,
          },
        },
      )
      .then((response: any) => response.data);
  }

  public static executeRetroactiveBilling(
    businessUnitId: string,
    invoiceCompetence: string,
    competences: string[],
    startDate: string,
    endDate: string,
  ): Promise<string> {
    return axios
      .post(`/api/v1/business-units/${businessUnitId}/calculations/perform-retroactive?invoiceCompetence=${invoiceCompetence}&competences=${competences.toString()}`)
      .then((response: any) => response.data);
  }

  public static executeBillingSelectedItems(
    billingContractsSelected: BillingContractsSelected,
  ): Promise<string> {
    const { businessUnitId, invoiceCompetence } = billingContractsSelected;
    const filter = billingContractsSelected.contractFilter;
    const param: ExecuteBillingContractSelectedRequestType = {
      businessUnitId,
      invoiceCompetence,
      beneficiaryId: filter.beneficiary,
      contractNumber: filter.contract,
      customerDocument: filter.customerDocument,
      customerIds: filter.customerIds,
      externalId: filter.externalId,
      instanceCode: filter.instanceCode,
      resultCenterId: filter.resultCenterId,
      periodStart: filter.periodStart ? DateHandler.formatToIsoDate(filter.periodStart) : null,
      periodEnd: filter.periodEnd ? DateHandler.formatToIsoDate(filter.periodEnd) : null,
      anniversaryPeriodStart: filter.anniversaryPeriodStart
        ? DateHandler.formatToIsoDate(filter.anniversaryPeriodStart) : null,
      anniversaryPeriodEnd: filter.anniversaryPeriodEnd
        ? DateHandler.formatToIsoDate(filter.anniversaryPeriodEnd) : null,
      status: filter.status,
      productIds: filter.productIds,
      markedToExpire: filter.markedToExpire,
      frequency: filter.frequency ? filter.frequency : null,
      isCustomBilling: filter.isCustomBilling,
      isReviewed: filter.isReviewed,
      robotFilterType: filter.robotFilterType,
      contractGroupId: filter.contractGroupId,
      contractGroupLinkedType: filter.contractGroupLinkedType,
      billingDaily: filter.billingDaily,
      billingAnniversaryType: filter.billingAnniversaryType,
      competenceFilter: filter.competenceFilter,
      expiresAtStart: filter.expiresAtStart,
      expiresAtEnd: filter.expiresAtEnd,
      filterInterval: filter.filterInterval,
      contractModel: filter.contractModel ? filter.contractModel : null,
    }

    return axios
      .post<ExecuteBillingContractSelectedRequestType, AxiosResponse<string>>(`/api/v1/business-units/${businessUnitId}/calculations/selected-items/perform`, param)
      .then(({ data }) => data);
  }

  public static executeRetroactiveBillingSelectedItems(
    billingContractsSelected: BillingContractsSelected,
    competences: string[],
    startDate: string,
    endDate: string,
  ): Promise<string> {
    const { businessUnitId, invoiceCompetence } = billingContractsSelected;
    const filter = billingContractsSelected.contractFilter;
    const param = {
      businessUnitId,
      invoiceCompetence,
      invoiceCompetenceStart: startDate,
      invoiceCompetenceEnd: endDate,
      beneficiaryId: filter.beneficiary,
      contractNumber: filter.contract,
      customerDocument: filter.customerDocument,
      customerIds: filter.customerIds,
      externalId: filter.externalId,
      instanceCode: filter.instanceCode,
      resultCenterId: filter.resultCenterId,
      periodEnd: filter.periodEnd,
      periodStart: filter.periodStart,
      anniversaryPeriodStart: filter.anniversaryPeriodStart
        ? DateHandler.formatToIsoDate(filter.anniversaryPeriodStart) : null,
      anniversaryPeriodEnd: filter.anniversaryPeriodEnd
        ? DateHandler.formatToIsoDate(filter.anniversaryPeriodEnd) : null,
      status: filter.status,
      productIds: filter.productIds,
      markedToExpire: filter.markedToExpire,
      competences,
      frequency: filter.frequency ? filter.frequency : null,
      isCustomBilling: filter.isCustomBilling,
      isReviewed: filter.isReviewed,
      robotFilterType: filter.robotFilterType,
      contractGroupId: filter.contractGroupId,
      contractGroupLinkedType: filter.contractGroupLinkedType,
      billingDaily: filter.billingDaily,
      contractModel: filter.contractModel,
    }
    return axios
      .post(`/api/v1/business-units/${businessUnitId}/calculations/selected-items/perform-retroactive`, param)
      .then(({ data }) => data);
  }

  public static deleteBilling(businessUnitId: string, billingId: string): Promise<void> {
    return axios
      .delete(`/api/v1/business-units/${businessUnitId}/invoices/${billingId}`);
  }

  public static deleteAllSelectedBilling(businessUnitId: string,
    listBillingId: Array<string>): Promise<string> {
    return axios
      .delete(`/api/v1/business-units/${businessUnitId}/invoices/invoices-selected-list`, {
        data: listBillingId,
      })
      .then((response: any) => response.data);
  }

  public static getInvoiceUrl(
    businessUnitId: string, invoiceId: string,
  ): Promise<BillingInvoiceUrl> {
    return axios
      .get(`/api/v1/business-units/${businessUnitId}/invoices/${invoiceId}`)
      .then((response: any) => BillingInvoiceUrl.of(response.data));
  }

  public static getInvoicePdf(
    businessUnitId: string, invoiceId: string,
  ): Promise<string> {
    return axios
      .get(`/api/v1/business-units/${businessUnitId}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
      })
      .then((response: any) => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        return URL.createObjectURL(blob);
      });
  }

  public static getAudits(
    businessUnitId: string,
    competence: string,
    pagination: Pagination,
  ): Promise<Response> {
    const params = {
      competence,
      page: pagination.page - 1,
      size: pagination.itemsPerPage,
      sortBy: pagination.arrangeSortBy,
    }

    return axios.get(`/api/v1/business-units/${businessUnitId}/customer-audits`, { params });
  }

  public static performAudit(businessUnitId: string, competence: string): Promise<Response> {
    return axios.post(`/api/v1/business-units/${businessUnitId}/customer-audits`, { competence });
  }
}
